import { useEffect, useState } from 'preact/hooks';
import type { MyEvent } from '../lib/api';
import { todayIsoAst } from '../lib/dates';
import { haptic } from '../lib/telegram';
import { DepositoPanel } from './DepositoPanel';

interface EventDetailModalProps {
  event: MyEvent;
  onClose: () => void;
  onCheckIn?: () => void;
  /** Triggered when the user picks "Necesito ayuda" — the existing cancel flow with penalty rules. */
  onCancel?: () => void;
  /** Triggered when the user picks "Encontré quien me cubra" — opens SwapPicker. */
  onSwap?: () => void;
  onChat?: () => void;
  onMiReporte?: () => void;
  onCerrar?: () => void;
  onBriefing?: () => void;
  /** Shown for a Completado event — opens the team results + ranking screen. */
  onResultados?: () => void;
  /**
   * True when this event is in the backend's "today" (actionable) bucket. That
   * bucket includes a just-finished event still awaiting closure — dated
   * yesterday but surfaced under HOY so the captain can still close it and staff
   * can still report. Time-sensitive actions gate on this, NOT on a strict
   * date===today match (which hid "Cerrar evento" the morning after, EV-0230).
   */
  actionable?: boolean;
  busy?: boolean;
}

const MONTH_ES_LONG = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
const DAY_ES = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];

function formatLongDate(iso: string): string {
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return iso;
  const d = new Date(`${iso}T12:00:00-04:00`);
  if (isNaN(d.getTime())) return iso;
  const dayName = DAY_ES[d.getDay()];
  const day = parseInt(m[3], 10);
  const month = MONTH_ES_LONG[parseInt(m[2], 10) - 1];
  return `${dayName} ${day} de ${month}, ${m[1]}`;
}

// Mirrors EventCard.tsx:formatTime — any fix to one MUST be ported to the
// other.
function formatTime(t: string): string {
  // t arrives from miniapp-api as "H:MM AM/PM" (Sheets text rows may carry a
  // lowercase "7:00 pm" or seconds "5:00:00 PM"), or as legacy 24h "HH:MM".
  // Never re-derive the meridiem when the string already has one — doing so
  // showed every PM call time as AM (EV-0428 / EV-0441, 2026-07-18).
  const ap = t.match(/^(\d{1,2})(?::(\d{2}))?(?::\d{2})?\s*([ap])\.?m\.?$/i);
  if (ap) {
    const h = parseInt(ap[1], 10) % 12 || 12;
    return `${h}:${ap[2] || '00'} ${ap[3].toLowerCase() === 'p' ? 'PM' : 'AM'}`;
  }
  // 24h "HH:MM" → "H:MM AM/PM"
  const m = t.match(/^(\d{1,2}):(\d{2})/);
  if (!m) return t;
  let h = parseInt(m[1], 10);
  const mm = m[2];
  const ampm = h >= 12 ? 'PM' : 'AM';
  h = h % 12 || 12;
  return `${h}:${mm} ${ampm}`;
}

export function EventDetailModal({ event, onClose, onCheckIn, onCancel, onSwap, onChat, onMiReporte, onCerrar, onBriefing, onResultados, actionable, busy }: EventDetailModalProps) {
  const [showCancelChooser, setShowCancelChooser] = useState(false);

  useEffect(() => {
    haptic('light');
    const onEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [onClose]);

  // Compare YYYY-MM-DD keys directly — device-local .toDateString() on the
  // pre-shifted "now" double-subtracts 4h on DR phones, so between 00:00 and
  // ~04:00 AST the event's actual day read as "not today" (EV-0230 family).
  const isToday = !!event.date && event.date === todayIsoAst();
  // Gate time-sensitive actions on the backend's actionable bucket (covers a
  // just-finished event still awaiting closure), falling back to the strict
  // date match when the prop isn't supplied so other callers keep their behavior.
  const canAct = actionable ?? isToday;
  const timeRange = [formatTime(event.startTime), event.endTime && formatTime(event.endTime)].filter(Boolean).join(' — ');

  // Single "No puedo ir" affordance branches into the chooser modal. Direct
  // cancel when the caller supplied onCancel without onSwap (today's event
  // after its call time: cancel still allowed, swap no longer). For a past
  // event the caller supplies neither and a short note replaces the buttons.
  function openNoPuedoIr() {
    if (onSwap) setShowCancelChooser(true);
    else if (onCancel) onCancel();
  }

  return (
    <div class="modal-bg show" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div class="modal">
        <div class="modal-handle" />
        <div class="modal-body">
          <h2>{event.title}</h2>
          <div class="badges" style={{ margin: '8px 0 14px' }}>
            {isToday && <span class="live-pill">Hoy</span>}
            <span class={`badge ${event.status === 'Confirmado' ? 'confirmado' : 'pendiente'}`}>{event.status}</span>
            {event.isCapitan && <span class="badge capitan">Capitán</span>}
            {event.checkedIn && <span class="badge confirmado">✓ Check-in hecho</span>}
          </div>

          <div class="detail-row">
            <div class="ic">📅</div>
            <div><div class="lbl">Fecha</div><div class="val">{formatLongDate(event.date)}</div></div>
          </div>
          {timeRange && (
            <div class="detail-row">
              <div class="ic">🕒</div>
              <div><div class="lbl">Horario</div><div class="val">{timeRange}</div></div>
            </div>
          )}
          {event.location && (
            <div class="detail-row">
              <div class="ic">📍</div>
              <div><div class="lbl">Ubicación</div><div class="val">{event.location}</div></div>
            </div>
          )}
          {event.role && (
            <div class="detail-row">
              <div class="ic">👤</div>
              <div><div class="lbl">Mi rol</div><div class="val">{event.role}</div></div>
            </div>
          )}

          <div class="modal-actions">
            {/* Resultados del equipo: the post-closure ranking, for a Completado event. */}
            {event.status === 'Completado' && onResultados && (
              <button class="btn primary" onClick={onResultados}>
                🏆 Resultados del equipo
              </button>
            )}
            {/* Client-deposit voucher: persistent home for the captain DM's deep-link.
                Self-hides for non-captain/non-Ops viewers (403). */}
            {event.status === 'Completado' && <DepositoPanel eventId={event.id} />}
            {canAct && !event.checkedIn && onCheckIn && (
              <button class="btn primary" disabled={busy} onClick={onCheckIn}>
                📍 Hacer check-in
              </button>
            )}
            {/* Mi reporte: only after check-in, only today, only if event isn't already closed. */}
            {canAct && event.checkedIn && event.status !== 'Completado' && onMiReporte && (
              <button class="btn primary" onClick={onMiReporte}>
                📝 Mi reporte
              </button>
            )}
            {/* Cerrar evento: captain-only affordance. The detail screen polls
                live state when opened. Visible today and only if not already closed. */}
            {canAct && event.isCapitan && event.status !== 'Completado' && onCerrar && (
              <button class="btn primary" onClick={onCerrar}>
                🏁 Cerrar evento
              </button>
            )}
            {onBriefing && (
              <button class="btn ghost" onClick={onBriefing}>
                📋 Briefing
              </button>
            )}
            {onChat && event.status !== 'Completado' && (
              <button class="btn ghost" onClick={onChat}>
                💬 Chat del equipo
              </button>
            )}
            {/* Cambiar turno: first-class, discoverable swap entry point — same
                event-state gating as "No puedo ir" below, but skips the chooser
                and opens SwapPicker directly since that's the non-destructive path. */}
            {event.status !== 'Completado' && !event.status.startsWith('Cancelado') && onSwap && (
              <button class="btn ghost" onClick={onSwap}>
                🔄 Cambiar turno
              </button>
            )}
            {event.status !== 'Completado' && !event.status.startsWith('Cancelado') && (onCancel || onSwap) && (
              <button class="btn danger-soft" onClick={openNoPuedoIr}>
                ❌ No puedo ir
              </button>
            )}
            {event.status !== 'Completado' && !event.status.startsWith('Cancelado') && !onCancel && !onSwap && (
              <p class="mireporte-meta">Ya no se puede cambiar ni cancelar este turno desde la app. Si hay un problema, habla con Angie o Wendy.</p>
            )}
            <button class="btn ghost" onClick={onClose}>Cerrar</button>
          </div>
        </div>
      </div>

      {showCancelChooser && (
        <div class="modal-bg show" onClick={(e) => { if (e.target === e.currentTarget) setShowCancelChooser(false); }}>
          <div class="modal" style={{ maxWidth: 420 }}>
            <div class="modal-body">
              <h3 style={{ margin: 0 }}>¿Cómo quieres manejarlo?</h3>
              <p class="mireporte-meta" style={{ marginTop: 8 }}>
                Asignado significa que ya cuentas con el turno. {onCancel ? 'Si no puedes ir, hay dos formas:' : 'Si no puedes ir, la vía es encontrar quien te cubra:'}
              </p>

              <div class="cancel-chooser-options">
                {onSwap && (
                  <button
                    class="cancel-chooser-card"
                    onClick={() => { setShowCancelChooser(false); onSwap(); }}
                  >
                    <div class="cancel-chooser-icon">🔄</div>
                    <div class="cancel-chooser-label">
                      <div class="cancel-chooser-title">Encontré quien me cubra</div>
                      <div class="cancel-chooser-sub">Sin penalidad si alguien acepta.</div>
                    </div>
                  </button>
                )}
                {onCancel && (
                  <button
                    class="cancel-chooser-card cancel-chooser-card-warn"
                    onClick={() => { setShowCancelChooser(false); onCancel(); }}
                  >
                    <div class="cancel-chooser-icon">🆘</div>
                    <div class="cancel-chooser-label">
                      <div class="cancel-chooser-title">Necesito ayuda con el reemplazo</div>
                      <div class="cancel-chooser-sub">⚠️ Penalidad RD$ 2,500 + strike si faltan &lt; 24h.</div>
                    </div>
                  </button>
                )}
              </div>

              <div class="modal-actions" style={{ marginTop: 12 }}>
                <button class="btn ghost" onClick={() => setShowCancelChooser(false)}>
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
